import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  MatchColumns,
  MatchItem,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = { words: GameWord[]; onExit?: () => void };

const WordMatchingGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();

  const left = useMemo(
    () =>
      shuffle(words.slice(0, 6).map((w) => ({ id: w.uid, label: w.palavra }))),
    [words]
  );
  const right = useMemo(
    () =>
      shuffle(
        words.slice(0, 6).map((w) => ({
          id: w.uid,
          label: w.descricao?.slice(0, 48) || w.palavra,
        }))
      ),
    [words]
  );

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [matched, setMatched] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<"ok" | "bad" | null>(null);

  const done = matched.length === left.length && left.length > 0;

  const handleRight = async (rightId: string) => {
    if (!selectedLeft || matched.includes(rightId)) return;
    if (selectedLeft === rightId) {
      setMatched((m) => [...m, rightId]);
      setScore((s) => s + 1);
      setFeedback("ok");
      await addPoints("GAME_MEMORY");
    } else {
      setFeedback("bad");
    }
    setSelectedLeft(null);
    window.setTimeout(() => setFeedback(null), 800);
  };

  return (
    <MobileLayout
      headerTitle="Ligar Palavras"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done ? (
          <CompleteCard>
            <GameTitle>Tudo ligado!</GameTitle>
            <ProgressText>Pares: {score}</ProgressText>
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Ligue a palavra à descrição correspondente
            </ProgressText>
            <MatchColumns>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {left.map((item) => (
                  <MatchItem
                    key={item.id}
                    type="button"
                    $selected={selectedLeft === item.id}
                    $done={matched.includes(item.id)}
                    disabled={matched.includes(item.id)}
                    onClick={() => setSelectedLeft(item.id)}
                  >
                    {item.label}
                  </MatchItem>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {right.map((item) => (
                  <MatchItem
                    key={`r-${item.id}`}
                    type="button"
                    $done={matched.includes(item.id)}
                    disabled={matched.includes(item.id)}
                    onClick={() => handleRight(item.id)}
                  >
                    {item.label}
                  </MatchItem>
                ))}
              </div>
            </MatchColumns>
            {feedback ? (
              <Feedback $ok={feedback === "ok"}>
                {feedback === "ok" ? "Par correto!" : "Tente de novo"}
              </Feedback>
            ) : null}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default WordMatchingGame;
